import React, { useState } from 'react';

import { PROFILES_PERMISSIONS } from '../../../permissions';
import checkPermissions from '../CheckPermissions';
import { MemoryForm } from './MemoryForm';
import {
  exactTime,
  MemoryEntry,
  MemoryKind,
  STALE_NOTICE,
  approveMemory,
  asEdits,
  asOverride,
  createMemory,
  decidedBy,
  deleteMemory,
  dismissMemory,
  editMemory,
  entryDetail,
  isStale,
  kindLabel,
  proposedBy,
  scopeOf,
} from './memoryApi';

/** What a change did, for the host to refresh what depends on it. */
export interface MemoryChange {
  entry: MemoryEntry;
  /** The change rewrote this entry's model's notes block (or should have). */
  affectsNotes: boolean;
  /** rgip kept the change but could not rewrite the notes block this time. */
  notesSynced: boolean | null;
  /** A short message for the host to show above the list it re-reads. */
  notice?: string;
}

interface Props {
  tenant: string;
  entry: MemoryEntry;
  /** `can_edit` from rgip. Buttons also need Studio's ARCHITECT role. */
  canEdit: boolean;
  /**
   * The model on screen, for an entry inherited from a model it was copied from. Its
   * only action is "Override for this model": a new entry on this model that replaces
   * it here (`supersedes_id`) and leaves it on its source.
   */
  overrideFor?: string | null;
  /** What approving this proposal replaces, in words (`replacedText`). */
  replaces?: string | null;
  onChanged: (change: MemoryChange) => void;
}

/**
 * One entry, as a proposal to decide or an approved decision to change.
 *
 * A proposal shows where it came from: the user's own words it was drawn from and who or
 * what suggested it, because approving it is agreeing that is what they meant.
 */
export function MemoryItem({
  tenant,
  entry,
  canEdit,
  overrideFor,
  replaces,
  onChanged,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const proposed = entry.status === 'proposed';
  const inherited = !proposed && !!entry.inherited_from_model_id;
  const detail = entryDetail(entry);

  const run = async (
    action: () => Promise<MemoryEntry | void>,
    affectsNotes: boolean,
    /** A 404 means this entry changed elsewhere. Not so for a create (bad model id). */
    onEntry = true
  ) => {
    setBusy(true);
    setFailed(null);
    try {
      const result = await action();
      setEditing(false);
      setOverriding(false);
      setConfirming(false);
      onChanged({
        entry: result || entry,
        affectsNotes: affectsNotes && scopeOf(entry) === 'model',
        notesSynced: result ? result.notes_synced ?? null : null,
      });
    } catch (e) {
      if (onEntry && isStale(e)) {
        // Decided or changed elsewhere since this list was read: re-read it, and say
        // why the entry moved or went, rather than an error on a row about to vanish.
        onChanged({
          entry,
          affectsNotes: false,
          notesSynced: null,
          notice: STALE_NOTICE,
        });
      } else {
        setFailed(e instanceof Error ? e.message : 'That did not save.');
      }
    } finally {
      setBusy(false);
    }
  };

  const actions = canEdit && (inherited ? !!overrideFor : true) && (
    <div className="mem-actions">
      {inherited ? (
        <button
          type="button"
          className="mem-btn"
          disabled={busy}
          onClick={() => setOverriding(true)}
        >
          Override for this model
        </button>
      ) : proposed ? (
        <>
          <button
            type="button"
            className="mem-btn mem-btn--primary"
            disabled={busy}
            onClick={() =>
              run(() => approveMemory(tenant, entry.entry_id), true)
            }
          >
            Approve
          </button>
          <button
            type="button"
            className="mem-btn"
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
          <button
            type="button"
            className="mem-btn"
            disabled={busy}
            onClick={() =>
              run(() => dismissMemory(tenant, entry.entry_id), false)
            }
          >
            Dismiss
          </button>
        </>
      ) : confirming ? (
        <>
          <button
            type="button"
            className="mem-btn mem-btn--danger"
            disabled={busy}
            onClick={() =>
              run(() => deleteMemory(tenant, entry.entry_id), true)
            }
          >
            Delete
          </button>
          <button
            type="button"
            className="mem-btn"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Keep
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="mem-btn"
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
          <button
            type="button"
            className="mem-btn"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            Delete
          </button>
        </>
      )}
    </div>
  );

  return (
    <li
      className={`mem-item${proposed ? ' mem-item--proposed' : ''}`}
      data-testid={proposed ? 'memory-proposal' : 'memory-entry'}
    >
      {overriding && overrideFor ? (
        <MemoryForm
          initial={{
            kind: entry.kind as MemoryKind,
            text: entry.text,
            instead_of: entry.instead_of,
            reason: entry.reason,
          }}
          submitLabel="Override"
          busy={busy}
          onCancel={() => setOverriding(false)}
          onSubmit={(values) =>
            run(
              () =>
                createMemory(tenant, asOverride(values, entry, overrideFor)),
              true,
              false
            )
          }
        />
      ) : editing ? (
        <MemoryForm
          initial={{
            kind: entry.kind as MemoryKind,
            text: entry.text,
            instead_of: entry.instead_of,
            reason: entry.reason,
          }}
          submitLabel={proposed ? 'Approve' : 'Save'}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSubmit={(values) =>
            run(
              () =>
                proposed
                  ? approveMemory(tenant, entry.entry_id, asEdits(values))
                  : editMemory(tenant, entry.entry_id, asEdits(values)),
              true
            )
          }
        />
      ) : (
        <>
          <span className="mem-kind">{kindLabel(entry.kind)}</span>
          <p className="mem-text">{entry.text}</p>
          {detail && <p className="mem-detail">{detail}</p>}
          {proposed && replaces && <p className="mem-detail">{replaces}</p>}
          {proposed && entry.quote && (
            <blockquote className="mem-quote">“{entry.quote}”</blockquote>
          )}
          <p className="mem-source" title={exactTime(entry, proposed)}>
            {proposed ? proposedBy(entry) : decidedBy(entry)}
          </p>
          {confirming && <p className="mem-detail">Delete this decision?</p>}
          {actions && checkPermissions(PROFILES_PERMISSIONS.ARCHITECT, actions)}
        </>
      )}
      {failed && <p className="mem-failed">{failed}</p>}
    </li>
  );
}
